/* eslint-disable no-undef */
/* global $$epiforms, epi */
import { getWithExpiry, setWithExpiry, isNullOrUndefined, getUrlVars } from "/Assets/js/utilities.js";
import intlTelInput from "/Assets/libs/intl-tel-input/js/intlTelInput.js";
import * as tomSelect from "/Assets/libs/TomSelect/tom-select.complete.js";
import VanillaModal from "/Assets/libs/Modal/VanillaModal.js";
import FormCaptureBuilder from "./form-capture-builder.js";
import FormMetadataPopulator from "./form-metadata-populator.js";
import { wtbProductInterestData } from "./product-interest-data.js";
import "./selection-element.js";
import "./phone-number-element.js";

window.dataLayer = window.dataLayer || [];
const throwError = message => window.console.error(`OptimizelyForms: ${message}`);

class OptimizelyForms {
    constructor() {
        this.formCapturePayload = [];
        this.formStarted = false;
        this.modal = null;
    }

    setupCompleted(e) {
        this.form = e.currentTarget;

        this.setupModalForm();
        this.setupFormTracking();
        this.setupSerialNumberField(e);

        const metadataFieldsToPopulate = [
            "ht_preferredcommunicationlanguage",
            "ht_campaignsource",
            "campaign_source",
            "ht_leadsource",
            "ht_leadsourcedetail",
            "ht_materialshape",
            "ht_application",
            "ht_platform",
            "ht_technologyinterest",
            "ht_productclasstype",
            "ht_productmodelline"
        ];
        const formMetadataPopulator = new FormMetadataPopulator();
        formMetadataPopulator.initWithClassNames(metadataFieldsToPopulate);

        this.initWtbProductInterestOnChange();
        this.setWtbProductInterestFromUrl();
    }

    initWtbProductInterestOnChange() {
        const wtbProductInterestInput = document.querySelector("div.product_interest_text select");

        if (!wtbProductInterestInput)
            return;

        new tomSelect(wtbProductInterestInput, {
            onChange: value => {
                const metadataFieldsToPopulate = [
                    "ht_materialshape",
                    "ht_application",
                    "ht_platform",
                    "ht_technologyinterest",
                    "ht_productclasstype",
                    "ht_productmodelline"
                ];

                const selectedData = wtbProductInterestData[value];
                if (selectedData) {
                    const values = metadataFieldsToPopulate.map(field => selectedData[field] || "");
                    const formMetadataPopulator = new FormMetadataPopulator();
                    formMetadataPopulator.initWithArrayValues(metadataFieldsToPopulate, values);
                }
            }
        });
    }

    setWtbProductInterestFromUrl() {
        const urlVars = getUrlVars();
        var value = urlVars.category ? urlVars.category : "";

        if (isNullOrUndefined(value))
            return;

        const selectedData = wtbProductInterestData[value];
        if (selectedData) {
            const metadataFieldsToPopulate = [
                "ht_materialshape",
                "ht_application",
                "ht_platform",
                "ht_technologyinterest",
                "ht_productclasstype",
                "ht_productmodelline"
            ];
            const values = metadataFieldsToPopulate.map(field => selectedData[field] || "");
            const formMetadataPopulator = new FormMetadataPopulator();
            formMetadataPopulator.initWithArrayValues(metadataFieldsToPopulate, values);
        }

        const wtbProductInterestInput = document.querySelector("div.product_interest_text select");
        this.setSelectValue(wtbProductInterestInput, value);
    }

    setSelectValue(input, value) {
        const tomselect = input.tomselect;
        if (tomselect) {
            tomselect.clear();
            tomselect.setValue(value);
        }
    }

    showButtonSubmitting() {
        this.submitButton = this.form.querySelector(".submitbuttonelementblock button");
        if (!this.submitButton) return;

        this.submitButton.setAttribute("data-label-text", this.submitButton.innerHTML);
        this.submitButton.innerHTML = "";
        this.submitButton.classList.add("loading-spinner");
        this.submitButton.disabled = true;
    }

    showButtonDefault() {
        this.submitButton = this.form.querySelector(".submitbuttonelementblock button");
        if (!this.submitButton) return;

        const text = this.submitButton.dataset.labelText;
        if (text) {
            this.submitButton.innerHTML = text;
            this.submitButton.classList.remove("loading-spinner");
        }
        this.submitButton.disabled = false;
    }

    setupModalForm() {
        const primaryCtaForm = document.getElementById("primaryCtaForm");
        if (!primaryCtaForm) return;

        const primaryCtaModal = new VanillaModal("primaryCtaForm",
            {
                onOpen: (e) => {
                    const element = e.target;
                    const title = element.dataset.title;
                    const description = element.dataset.description;
                    const calltoaction = element.dataset.calltoaction;
                    const modal = document.getElementById("primaryCtaForm");

                    if (!isNullOrUndefined(title)) {
                        modal.querySelector(".Form__Title").textContent = title;
                    }
                    if (!isNullOrUndefined(description)) {
                        modal.querySelector(".Form__Description").innerHTML = `<p>${description}</p>`;
                    }
                    if (!isNullOrUndefined(calltoaction)) {
                        this.setCallToAction(calltoaction);
                    }
                }
            });
        this.modal = primaryCtaModal;
        this.modal.init();

    }

    setCallToAction(calltoaction = null) {
        if (isNullOrUndefined(calltoaction))
            return;

        const metadataFieldToPopulate = "ht_calltoaction";
        const formMetadataPopulator = new FormMetadataPopulator(metadataFieldToPopulate, calltoaction);
        formMetadataPopulator.populateInputs();
    }

    setupFormTracking() {
        const formElements = this.form.querySelectorAll("input, button, textarea, select");

        formElements.forEach(element => {
            element.addEventListener("click", this.trackFormStart.bind(this));
            element.addEventListener("blur", this.trackFormStart.bind(this));
            element.addEventListener("change", this.trackFormStart.bind(this));
        });
    }

    trackFormStart() {
        if (!this.formStarted) {
            const formName = this.form.getAttribute("name");
            window.dataLayer.push({
                event: "formStarted",
                formId: formName
            });
            this.formStarted = true;
        }
    }

    buildFormCapture(form, workingFormInfo) {
        let fromCaptureBuilder = new FormCaptureBuilder(form, workingFormInfo);
        this.formCapturePayload = fromCaptureBuilder.generateMappings();
    }

    submitFormCapture(form) {
        const formCaptureId = form.getAttribute("data-form-capture-id");
        const hasFormCaptureId = formCaptureId !== null && formCaptureId !== "";
        const formCaptureApiUrl = form.getAttribute("data-form-capture-api-url");

        if (!hasFormCaptureId) return;

        const serializedForm = d365mktformcapture.serializeForm(form, this.formCapturePayload);
        //console.log(JSON.stringify(serializedForm)); // NOTE: enable for debugging

        const payload = serializedForm.SerializedForm.build();

        //D365 form capture API expects the field name to be "Opted In" instead of "Opted Out". This is not setting correctly with current version of API so this is a workaround until it is fixed.
        const formName = form.getAttribute("name");
        if (formName === "ha-subscription-optin") {
            payload.data = payload.data.replaceAll("Opted Out", "Opted In");
        }

        const captureConfig = {
            FormId: formCaptureId,
            FormApiUrl: formCaptureApiUrl
        };

        d365mktformcapture.submitForm(captureConfig, payload);
    }

    setupSerialNumberField(e) {
        const $serialNumberField = $(e.currentTarget).find("div.serialNumber");
        var formatMasksCacheKey = "product-serial-number-format-masks";
        if ($serialNumberField.length) {
            const $serialNumberHelp = $(e.currentTarget).find(".serialNumberHelp");
            const $serialNumberHelpBlock = $serialNumberHelp.parent();

            if ($serialNumberHelp.length) {
                $serialNumberHelpBlock.hide();
                $(e.currentTarget).find(".serialNumberHelp").insertAfter($serialNumberField);
            }

            if (getWithExpiry(formatMasksCacheKey)) {
                return;
            } else {
                fetch("/InstallBase/GetSerialNumberFormatMasks", {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    }
                })
                    .then(response => response.json())
                    .then(data => {
                        setWithExpiry(formatMasksCacheKey, JSON.stringify(data), 1800);
                    })
                    .catch(() => {
                        throwError("Error fetching serial number format masks:", error);
                    });
            }
        }
    }

    stepValidating(e) {
        if (e.isValid) {
            return;
        }
    }

    startSubmitting(e) {
        const form = e.currentTarget;
        const workingFormInfo = e.workingFormInfo;
        const isMultiStep = workingFormInfo.$steps.length > 1;

        if (isMultiStep) {
            const currentStepIndex = form.querySelector("input[name=__FormCurrentStepIndex]").value;
            const lastStep = workingFormInfo.$steps.length - 1;

            if (currentStepIndex != lastStep)
                return;
        }

        this.showButtonSubmitting();
        this.buildFormCapture(form, workingFormInfo);
    }

    submitted(e) {
        const self = this;
        if (!e.isSuccess || !e.isFinalizedSubmission) {
            this.showButtonDefault();
            return;
        }

        const form = e.currentTarget;
        this.submitFormCapture(form);

        var formType = "Other";
        const leadGen = $(e.currentTarget).find('input[value="Lead Gen"]');
        const leadCapture = $(e.currentTarget).find('input[value="Lead Capture"]');
        const support = $(e.currentTarget).find("input[value=Support]");
        const registration = $(e.currentTarget).find("input[value=Registration]");
        const productRegistration = $(e.currentTarget).find('input[value="Product Registration"]');
        if (leadGen.length || leadCapture.length) {
            formType = "Lead Capture";
        } else if (support.length) {
            formType = support.val();
        } else if (registration.length || productRegistration.length) {
            formType = "Registration";
        }

        if (self.modal) {
            setTimeout(function () {
                self.modal.close();
            }, 5000);
        }

        var formName = $(e.currentTarget).attr("name");
        formName = formName ? formName : e.workingFormInfo.Name;
        if (e.isFinalizedSubmission && e.isSuccess) {
            window.dataLayer.push({
                event: "formSubmissionSuccess",
                formId: formName,
                formType: formType
            });
        }
    }
}

if (typeof $$epiforms !== "undefined") {
    (function ($) {
        epi.EPiServer.Forms.AsyncSubmit = true;
        const optimizelyForms = new OptimizelyForms();

        $.extend(true, epi.EPiServer.Forms, {
            Validators: {
                "Hypertherm.Web.Infrastructure.Forms.Validation.SerialNumberValidator": function (
                    fieldName,
                    fieldValue,
                    validatorMetaData
                ) {
                    var validateResult = { isValid: false };
                    const $element = $(`input[name=${fieldName}]`);
                    const placeholder = $element.attr("placeholder");
                    const maskPlaceholder = placeholder.replace(/_/g, "").trim();

                    if (fieldValue.length) {
                        if (fieldValue === maskPlaceholder) {
                            validateResult.isValid = false;
                            validateResult.message = validatorMetaData.model.message;
                        } else {
                            $.ajax({
                                async: false,
                                url: `/InstallBase/ValidateSerialNumber?serialNumber=${fieldValue}`,
                                success: function (valid) {
                                    validateResult.isValid = valid;
                                    if (!validateResult.isValid)
                                        validateResult.message = validatorMetaData.model.message;
                                },
                                error: function () {
                                    validateResult.isValid = false;
                                    validateResult.message = validatorMetaData.model.message;
                                }
                            });
                        }
                    } else {
                        validateResult.isValid = true;
                    }
                    return validateResult;
                },
                "Hypertherm.Web.Infrastructure.Forms.Validation.PhoneNumberValidator": function (fieldName,
                    fieldValue,
                    validatorMetaData) {
                    const validateResult = { isValid: false };

                    if (fieldValue.length) {
                        const element = document.querySelector(`[data-f-element-name="${fieldName}"] [data-phone-input]`);
                        const iti = intlTelInput.getInstance(element);
                        const isValid = iti.isValidNumber();

                        if (isValid) {
                            validateResult.isValid = true;
                        } else {
                            validateResult.isValid = false;
                            validateResult.message = validatorMetaData.model.message;
                        }
                    } else {
                        validateResult.isValid = true;
                    }

                    return validateResult;
                },
                "Hypertherm.Web.Infrastructure.Forms.Validation.TurnstileValidator": function () {
                    return { isValid: true }; // return true here, validation happens on the server side only
                }
            },
            CustomBindingElements: {
                "Hypertherm.Web.Features.Forms.Models.DateTimeElementBlock": function (
                    elementInfo,
                    val
                ) {
                    if (!val || $.type(val) !== "string" || !Date.parse(val)) {
                        return;
                    } else {
                        return val;
                    }
                }
            }
        });

        $$epiforms(".EPiServerForms")
            .on("formsSetupCompleted", optimizelyForms.setupCompleted.bind(optimizelyForms))
            .on("formsStepValidating", optimizelyForms.stepValidating.bind(optimizelyForms))
            .on("formsStartSubmitting", optimizelyForms.startSubmitting.bind(optimizelyForms))
            .on("formsSubmitted", optimizelyForms.submitted.bind(optimizelyForms));

    })($$epiforms || $);
}